import { Array as EffectArray, Record as EffectRecord } from 'effect'
import { z } from 'zod'

import type { ChatItem } from './sessionChat'

// プランのカード（PdM の要件 C-8、仕様の「プラン単位の承認」）の中身と、利用者の判断の文面。
// Agent Lab の承認の契約（salescore-agent-lab の PR #11 の src/kpiPlan.ts、ADR-0015）に形と文面をそろえる。
// エージェントは書き込みの前に、回答の本文に ```kpi-plan のブロックを1つ出す。利用者が項目ごとに選んだ結果は、決まった文面の次の発言として送る。
// runtime がその発言から承認した項目を読み、合うツールの呼び出しを確認なしで通すのは、runtime の対応の後（#10114 の追記）。
// それまでは、外した項目も含めてツールごとの承認のカードで止まる

export const KPI_PLAN_OPS = ['create', 'update', 'archive', 'restore', 'goal', 'group'] as const
export const KPI_PLAN_KINDS = [
  'kpi',
  'dashboard',
  'sheet',
  'custom_view',
  'custom_object',
  'goal_config',
  'user_group',
  'dimension_group',
] as const

// 1つのプランの項目の上限（超える一括の操作は targets に束ねる約束）
const KPI_PLAN_MAX_ITEMS = 30

const trimmedLines = z
  .array(z.string())
  .transform((lines) => lines.map((line) => line.trim()).filter((line) => line !== ''))

const kpiPlanItemSchema = z.object({
  // 発言に埋めて往復させるので、区切り（空白、句読点）を含まない文字に限る
  id: z.string().regex(/^[\w\-]{1,16}$/v),
  op: z.enum(KPI_PLAN_OPS),
  kind: z.enum(KPI_PLAN_KINDS),
  name: z.string().trim().min(1).max(200),
  // 既にある設定物を変えるときの対象。作るときにはない
  viewId: z.string().trim().min(1).max(200).optional(),
  // 値に効く設定（対象、集計、絞り込み、軸）を書いた 2〜6 行
  details: trimmedLines,
  // 一括の操作（N 本に同じ変更）の対象
  targets: z.array(z.object({ name: z.string().trim().min(1).max(200), viewId: z.string().optional() })).optional(),
})

const kpiPlanSchema = z.object({
  planId: z.string().regex(/^[\w\-]{1,64}$/v),
  title: z.string().trim().min(1).max(200).catch('変更のプラン'),
  items: z
    .array(kpiPlanItemSchema)
    .min(1)
    .max(KPI_PLAN_MAX_ITEMS)
    .refine((items) => EffectArray.dedupe(items.map(({ id }) => id)).length === items.length),
})

export type KpiPlan = z.infer<typeof kpiPlanSchema>
export type KpiPlanItem = KpiPlan['items'][number]

// ブロックの中身にバッククォートは入らない（JSON の文字列にも書かない約束）ので、次の ``` までを中身とする
const PLAN_BLOCK_PATTERN = /```kpi-plan(?<body>[^`]*)```/gv
const EXTRA_BLANK_LINES_PATTERN = /\n{3,}/gv

const parseKpiPlan = (body: string): KpiPlan | null => {
  try {
    const parsed = kpiPlanSchema.safeParse(JSON.parse(body.trim()))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

/**
 * 回答の本文から、最後の有効な kpi-plan のブロックを読み、本文からそのブロックを外して返す。
 * 壊れたブロックはカードにせず、本文のまま残す（利用者は文で読める。runtime も承認に使わない）
 */
export const splitKpiPlan = (text: string): { plan: KpiPlan | null; text: string } => {
  const found = [...text.matchAll(PLAN_BLOCK_PATTERN)]
    .map((match) => ({ block: match[0], plan: parseKpiPlan(match.groups?.body ?? '') }))
    .findLast(({ plan }) => plan !== null)
  if (found === undefined) {
    return { plan: null, text }
  }
  return { plan: found.plan, text: text.replace(found.block, '').replaceAll(EXTRA_BLANK_LINES_PATTERN, '\n\n').trim() }
}

export interface KpiPlanDecision {
  planId: string
  decision: 'execute' | 'cancel'
  executed: readonly string[]
  excluded: readonly string[]
  revisions: ReadonlyArray<{ id: string; text: string }>
}

/**
 * 利用者の判断を、次の発言として送る決まった文面にする（runtime と指示書がこの形を読む）
 */
export const formatKpiPlanDecision = (decision: KpiPlanDecision): string => {
  if (decision.decision === 'cancel') {
    return `プラン ${decision.planId} は実行しません。`
  }
  const excluded = decision.excluded.length === 0 ? 'なし' : decision.excluded.join(', ')
  const head = `プラン ${decision.planId} を承認します。実行: ${decision.executed.join(', ')}。除外: ${excluded}。`
  // 修正点は1項目1行にする（本文に句点を含んでも区切りがぶれないように）
  const revisions = decision.revisions.map(
    ({ id, text }) =>
      `修正: ${id} = ${text
        .split('\n')
        .map((line) => line.trim())
        .join(' ')}`,
  )
  return [head, ...revisions].join('\n')
}

const CANCEL_PATTERN = /プラン\s*(?<planId>[\w\-]{1,64})\s*は実行しません/v
const APPROVE_PATTERN = /プラン\s*(?<planId>[\w\-]{1,64})\s*を承認します/v
const EXECUTED_PATTERN = /実行[:：]\s*(?<ids>[^。\n]*)/v
const EXCLUDED_PATTERN = /除外[:：]\s*(?<ids>[^。\n]*)/v
const REVISION_PATTERN = /^修正[:：]\s*(?<id>[\w\-]{1,16})\s*=(?<text>.+)$/gmv
const ITEM_ID_PATTERN = /^[\w\-]{1,16}$/v

const splitIds = (raw: string | undefined): readonly string[] =>
  (raw ?? '')
    .split(/[,、\s]+/v)
    .map((token) => token.trim())
    .filter((token) => token !== '' && token !== 'なし' && ITEM_ID_PATTERN.test(token))

/**
 * 発言がプランへの判断なら読み取る。そうでなければ null を返す。会話の発言から、カードに決めた結果を出すのに使う
 */
export const parseKpiPlanDecision = (text: string): KpiPlanDecision | null => {
  const cancelled = CANCEL_PATTERN.exec(text)?.groups?.planId
  if (cancelled !== undefined) {
    return { planId: cancelled, decision: 'cancel', executed: [], excluded: [], revisions: [] }
  }
  const approved = APPROVE_PATTERN.exec(text)?.groups?.planId
  if (approved === undefined) {
    return null
  }
  return {
    planId: approved,
    decision: 'execute',
    executed: splitIds(EXECUTED_PATTERN.exec(text)?.groups?.ids),
    excluded: splitIds(EXCLUDED_PATTERN.exec(text)?.groups?.ids),
    revisions: [...text.matchAll(REVISION_PATTERN)].flatMap((match) => {
      const id = match.groups?.id
      const body = match.groups?.text?.trim() ?? ''
      return id === undefined || body === '' ? [] : [{ id, text: body }]
    }),
  }
}

/**
 * 会話の指示から、プランごとの判断を集める。キーはプランの ID。同じプランに2回答えていたら、後の判断を使う
 */
export const kpiPlanDecisionsOf = (items: readonly ChatItem[]): Readonly<Record<string, KpiPlanDecision>> =>
  EffectRecord.fromEntries(
    items.flatMap((item) => {
      const decision = item.kind === 'user' ? parseKpiPlanDecision(item.text) : null
      return decision === null ? [] : [[decision.planId, decision] as const]
    }),
  )

/**
 * 判断を送れるプランの ID。実行中でなく、最後の回答がプランを含み、その後に指示を送っていないときだけ（仕様の「プラン単位の承認」）。
 * プランの後に別の回答や指示があれば、エージェントがもう別の話に進んでいるためである
 */
export const actionableKpiPlanIdOf = (items: readonly ChatItem[], runningTurnId: string | null): string | null => {
  if (runningTurnId !== null) {
    return null
  }
  const lastAnswerIndex = items.findLastIndex((item) => item.kind === 'assistant')
  const lastAnswer = items.at(lastAnswerIndex)
  return lastAnswerIndex !== -1 &&
    lastAnswerIndex > items.findLastIndex((item) => item.kind === 'user') &&
    lastAnswer?.kind === 'assistant'
    ? (splitKpiPlan(lastAnswer.text).plan?.planId ?? null)
    : null
}
